/**
 * View track helpers for Medical Necessity analytics.
 * @module medicalNecessityAnalytics/viewTrackers
 */
import { MutableRefObject } from "react";
import { NCCNEvaluationRecord } from "../../../data-model/nccnEvaluation";
import {
  EvaluationItem,
  EvaluationResult,
} from "../../../data-model/nccnGuidelines";
import { EventsName } from "../../../enums/eventsName";
import { buildLocationAttrs } from "./locationAttrs";
import {
  mapAiCriterionAnswer,
  mapAiGuidelineVerdict,
  mapOrderVerdict,
  countGuidelineVerdicts,
} from "./mappers";
import { MedicalNecessityAnalyticsSession } from "./sessionState";

/** Shared deps for view trackers. */
export interface ViewTrackerDeps {
  analysisId: string;
  session: MedicalNecessityAnalyticsSession;
  emit: (eventName: EventsName, additional: Record<string, unknown>) => void;
}

/**
 * Clears a pending action-id ref when it still matches the id just emitted.
 *
 * @param pendingActionIdRef - Optional handoff ref from navigation / override
 * @param actionId - Action id attached to the emitted view event
 */
const clearPendingActionId = (
  pendingActionIdRef: MutableRefObject<string | undefined> | undefined,
  actionId: string | undefined,
): void => {
  if (!pendingActionIdRef || !actionId) return;
  if (pendingActionIdRef.current === actionId) {
    pendingActionIdRef.current = undefined;
  }
};

/**
 * Builds analysis / guideline / criterion / verdict / summary view trackers.
 *
 * @param deps - Analysis ID, session, and emit
 * @returns View track methods
 */
export const createViewTrackers = ({
  analysisId,
  session,
  emit,
}: ViewTrackerDeps) => {
  const trackPanelOpened = (
    guidelineCount: number,
    guidelineIndex: number,
  ) => {
    emit(EventsName.MEDICAL_NECESSITY_PANEL_OPENED, {
      guideline_count: guidelineCount,
      guideline_index: guidelineIndex,
    });
  };

  /**
   * Emits `panel_closed` when the reviewer closes the panel (the
   * review-completion anchor).
   *
   * @param aiOrderVerdict - Order-level AI verdict
   * @param humanOrderVerdict - Order-level human verdict
   */
  const trackPanelClosed = (
    aiOrderVerdict: EvaluationResult | null | undefined,
    humanOrderVerdict: EvaluationResult | null | undefined,
  ) => {
    emit(EventsName.MEDICAL_NECESSITY_PANEL_CLOSED, {
      ai_order_verdict: mapOrderVerdict(aiOrderVerdict),
      human_order_verdict: mapOrderVerdict(humanOrderVerdict),
    });
  };

  /**
   * Emits Guideline Viewed once per identity; clears pending action id only
   * when the event actually fires.
   *
   * @param guideline - Current guideline
   * @param guidelineIndex - One-based display index
   * @param identity - Dedup key for this view
   * @param actionId - Optional correlating click action id
   * @param pendingActionIdRef - Optional ref to clear after a real emit
   */
  const trackGuidelineViewed = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    identity: string,
    actionId?: string,
    pendingActionIdRef?: MutableRefObject<string | undefined>,
  ) => {
    if (!session.claimViewIdentity("guideline", identity)) return;
    session.setRhsStage("criterion");
    const loc = buildLocationAttrs(analysisId, guideline, guidelineIndex);
    session.setLocationSnapshot(loc);
    emit(EventsName.MEDICAL_NECESSITY_GUIDELINE_VIEWED, {
      ...loc,
      ...(actionId ? { action_id: actionId } : {}),
      ai_guideline_verdict: mapAiGuidelineVerdict(
        guideline.metadata?.evaluation?.final_result,
      ),
    });
    clearPendingActionId(pendingActionIdRef, actionId);
  };

  /**
   * Emits Criterion Viewed once per identity; clears pending action id only
   * when the event actually fires.
   *
   * @param guideline - Current guideline
   * @param guidelineIndex - One-based display index
   * @param item - Current criterion
   * @param criterionIndex - One-based criterion index
   * @param identity - Dedup key for this view
   * @param actionId - Optional correlating click action id
   * @param pendingActionIdRef - Optional ref to clear after a real emit
   */
  const trackCriterionViewed = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    item: EvaluationItem,
    criterionIndex: number,
    identity: string,
    actionId?: string,
    pendingActionIdRef?: MutableRefObject<string | undefined>,
  ) => {
    if (!session.claimViewIdentity("criterion", identity)) return;
    session.setRhsStage("criterion");
    const visit = session.nextCriterionVisit(
      guideline.guideline_id ?? "",
      item.id,
    );
    const loc = buildLocationAttrs(
      analysisId,
      guideline,
      guidelineIndex,
      item,
      criterionIndex,
      visit,
    );
    session.setLocationSnapshot(loc);
    emit(EventsName.MEDICAL_NECESSITY_CRITERION_VIEWED, {
      ...loc,
      ...(actionId ? { action_id: actionId } : {}),
      ai_criterion_answer: mapAiCriterionAnswer(item.verdict),
      ai_guideline_verdict: mapAiGuidelineVerdict(
        guideline.metadata?.evaluation?.final_result,
      ),
    });
    clearPendingActionId(pendingActionIdRef, actionId);
  };

  /**
   * Emits Guideline Verdict Viewed once per identity; clears pending action id
   * only when the event actually fires.
   *
   * @param guideline - Current guideline
   * @param guidelineIndex - One-based display index
   * @param identity - Dedup key for this view
   * @param actionId - Optional correlating click action id
   * @param pendingActionIdRef - Optional ref to clear after a real emit
   */
  const trackGuidelineVerdictViewed = (
    guideline: NCCNEvaluationRecord,
    guidelineIndex: number,
    identity: string,
    actionId?: string,
    pendingActionIdRef?: MutableRefObject<string | undefined>,
  ) => {
    if (!session.claimViewIdentity("verdict", identity)) return;
    session.setRhsStage("guideline_verdict");
    const loc = buildLocationAttrs(analysisId, guideline, guidelineIndex);
    session.setLocationSnapshot(loc);
    emit(EventsName.MEDICAL_NECESSITY_GUIDELINE_VERDICT_VIEWED, {
      ...loc,
      ...(actionId ? { action_id: actionId } : {}),
      ai_guideline_verdict: mapAiGuidelineVerdict(
        guideline.metadata?.evaluation?.final_result,
      ),
    });
    clearPendingActionId(pendingActionIdRef, actionId);
  };

  /**
   * Emits Summary Viewed once per identity; clears pending action id only when
   * the event actually fires.
   *
   * @param guidelines - All guideline records for the order
   * @param aiOrderVerdict - Order-level AI verdict
   * @param humanOrderVerdict - Order-level human verdict
   * @param identity - Dedup key for this view
   * @param actionId - Optional correlating click action id
   * @param pendingActionIdRef - Optional ref to clear after a real emit
   */
  const trackSummaryViewed = (
    guidelines: NCCNEvaluationRecord[],
    aiOrderVerdict: EvaluationResult | null | undefined,
    humanOrderVerdict: EvaluationResult | null | undefined,
    identity: string,
    actionId?: string,
    pendingActionIdRef?: MutableRefObject<string | undefined>,
  ) => {
    if (!session.claimViewIdentity("summary", identity)) return;
    session.setRhsStage("summary");
    session.setLocationSnapshot({});
    const { met, notMet } = countGuidelineVerdicts(guidelines);
    emit(EventsName.MEDICAL_NECESSITY_SUMMARY_VIEWED, {
      ...(actionId ? { action_id: actionId } : {}),
      guideline_count: guidelines.length,
      guideline_met_count: met,
      guideline_not_met_count: notMet,
      ai_order_verdict: mapOrderVerdict(aiOrderVerdict),
      human_order_verdict: mapOrderVerdict(humanOrderVerdict),
    });
    clearPendingActionId(pendingActionIdRef, actionId);
  };

  return {
    trackPanelOpened,
    trackPanelClosed,
    trackGuidelineViewed,
    trackCriterionViewed,
    trackGuidelineVerdictViewed,
    trackSummaryViewed,
  };
};
